// Tenantra Inc. - Shared building blocks for slide-in detail panels
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from '@/components/ui/sheet';

export const copyText = async (text) => {
  try { await navigator.clipboard.writeText(text); toast.success('Link copied'); }
  catch { toast.error('Could not copy. Select the link and copy it manually.'); }
};

export const fmtDate = (d) => {
  if (!d) return 'Not set';
  const [y, m, day] = d.split('T')[0].split('-');
  return new Date(y, m - 1, day).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const Field = ({ label, children, wide }) => (
  <div className={`min-w-0 ${wide ? 'col-span-2' : ''}`}>
    <dt className="text-[11px] text-slate-400 mb-0.5">{label}</dt>
    <dd className="text-sm text-slate-800 break-words">{children}</dd>
  </div>
);

export const Section = ({ title, aside, children }) => (
  <section className="px-6 py-5 border-b border-slate-100">
    <div className="flex items-baseline justify-between mb-3">
      <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{title}</h3>
      {aside && <span className="text-xs text-slate-400">{aside}</span>}
    </div>
    {children}
  </section>
);

export const StatusText = ({ cfg }) => (
  <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${cfg.cls}`}>
    <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />{cfg.label}
  </span>
);

export const PrimaryAction = ({ onClick, children, testId }) => (
  <button onClick={onClick} className="h-9 px-4 inline-flex items-center gap-1.5 rounded-md bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium transition-colors" data-testid={testId}>{children}</button>
);

export const SecondaryAction = ({ onClick, children, testId }) => (
  <button onClick={onClick} className="h-9 px-4 inline-flex items-center gap-1.5 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium transition-colors" data-testid={testId}>{children}</button>
);

export function DetailSheet({ open, onClose, loading, crumb, title, meta, footer, children, testId }) {
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-[520px] p-0 overflow-y-auto bg-white" data-testid={testId}>
        {loading ? (
          <div className="p-6 space-y-3">
            <SheetTitle className="sr-only">Loading</SheetTitle>
            <SheetDescription className="sr-only">Loading details</SheetDescription>
            {[0, 1, 2, 3].map(i => <div key={i} className="h-4 bg-slate-100 rounded animate-pulse" style={{ width: `${80 - i * 12}%` }} />)}
          </div>
        ) : (
          <>
            <div className="px-6 pt-6 pb-5 border-b border-slate-200">
              <p className="text-[11px] text-slate-400 mb-1">{crumb}</p>
              <SheetTitle className="text-xl font-semibold text-slate-900 pr-8" data-testid={`${testId}-title`}>{title}</SheetTitle>
              <SheetDescription asChild><div className="flex items-center gap-3 mt-1 text-xs">{meta}</div></SheetDescription>
            </div>
            {children}
            {footer && <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex flex-wrap items-center gap-3">{footer}</div>}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
